import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { InterPoint, Logger } from '@saviynt/common';
import {
  Box,
  Link as DSLink,
  ListRow,
  ListRowGroup,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodAccountChip,
  SodColumnChip,
  SodListLoader,
  SodMessage,
  SodReloader,
  SodUserChip,
} from '../../../../../../../shared/components';
import {
  ABORT_ERROR,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_MED,
  ENTITLEMENT_DETAILS_TABS,
  SEARCH_TEXT_LENGTH,
  testIds,
  violationsEntitlement,
} from '../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useInViewObserver,
  useNavigateTo,
  useTypedSelector,
} from '../../../../../../../shared/hooks';
import { type ListTrackerState } from '../../../../../../../shared/types';
import { createDevRejectionHandler } from '../../../../../../../shared/utils';
import {
  type EntitlementUsersSidePanelGroupRowContextState,
  type EntitlementUsersSidePanelRowContextState,
} from '../../../../../data-layer/detailsTypes';
import { detailsActions } from '../../../../../data-store/detailsSlice';
import {
  type GetUsersRequest,
  useGetUsersMutation,
  type UserItem,
} from '../../data-layer';
import { type TrackedUserRowType } from '../../hooks';
import {
  configureUsersTracker,
  getUserRowContext,
  getUsersListPayload,
} from '../../utils';

import './UsersList.css';

const MAX_COUNT = 25;
const LEAVE_ANIM_DURATION = 600;

interface UsersListProps {
  userIds: number[];
  entKeys: number[];
  riskKeys: number[];
}

/**
 * Entitlement Users List Component
 * Main container component for displaying and managing users associated with entitlements
 *
 * @component
 * @description Orchestrates the users feature including data fetching, filtering, search,
 * list display, and side panel interactions. Integrates with multiple APIs and manages complex
 * state for user data presentation and user interactions.
 *
 * @returns {ReactElement} Memoized main users component with full feature set
 */
function UsersList({
  userIds,
  entKeys,
  riskKeys,
}: UsersListProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    aag: {
      details: {
        activeTab,
        entitlementSummary: {
          selectedEntitlement,
          data: selectedEntitlementData,
        },
        entitlementUsers: {
          list: { totalRecords: usersTotalRecords, rowIdToTransition },
          controls: { activeFilters, searchKey, activeColumns, sortInAction },
          sidepanel: { isSidePanelVisible },
        },
      },
      global: { isGlobalLoading },
    },
  } = useTypedSelector((state) => state);

  // API endpoints
  const [getUsers, { isLoading: isListLoading }] = useGetUsersMutation();
  const { navigateToDetails } = useNavigateTo();

  // Offset, observer and lists
  const abortRef = useRef<((reason: string) => void) | null>(null);
  const requestInProgressRef = useRef<boolean>(false);

  // Infinite scroll state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isObserverInView, setIsObserverInView] = useState(true);
  const [isListError, setIsListError] = useState(false);
  const [listRows, setListRows] = useState([] as TrackedUserRowType[]);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  // Constants for infinite scroll
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        usersTotalRecords === null ||
          (typeof usersTotalRecords === 'number' &&
            currentOffset < usersTotalRecords &&
            listRows.length < usersTotalRecords)
      ),
    [currentOffset, usersTotalRecords, listRows.length]
  );
  const isListZero = useMemo(
    () => Boolean(listRows.length === 0),
    [listRows.length]
  );
  const isZeroCount = useMemo(
    () =>
      Boolean(
        isListZero &&
          usersTotalRecords !== null &&
          !isValidApiCall &&
          !isListLoading
      ),
    [usersTotalRecords, isListZero, isValidApiCall, isListLoading]
  );
  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  useEffect(() => {
    dispatch(detailsActions.setEntitlementUsersLoading(isListLoading));
  }, [dispatch, isListLoading]);

  // Extract complex expressions for dependency array
  const userIdsString = useMemo(() => JSON.stringify(userIds), [userIds]);
  const entKeysString = useMemo(() => JSON.stringify(entKeys), [entKeys]);
  const riskKeysString = useMemo(() => JSON.stringify(riskKeys), [riskKeys]);

  // List API call
  const handleListApiCall = useCallback(
    async (offset: number) => {
      // Prevent duplicate requests
      if (requestInProgressRef.current) {
        return Promise.reject(new Error('Request already in progress'));
      }

      requestInProgressRef.current = true;
      abortRef.current?.('New Search');

      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const getFilterPayload = getUsersListPayload(
        activeFiltersMap,
        sortInAction,
        { max: MAX_COUNT, offset }
      );
      const payload: GetUsersRequest = {
        includeDetails: true,
        includeViolationSummary: true,
        userKeys: userIds,
        ...(entKeys.length > 0 && { entitlementKeys: entKeys }),
        ...(riskKeys.length > 0 && { riskKeys }),
        columnToSort: getFilterPayload.sort.field,
        globalSearch: searchKey ?? '',
        max: getFilterPayload.pagination.max,
        offset: getFilterPayload.pagination.offset,
        order: getFilterPayload.sort.order,
        ...getFilterPayload.filters,
      };

      try {
        const controller = getUsers(payload);

        abortRef.current = () => {
          controller.abort();
          requestInProgressRef.current = false;
        };

        const response = (await controller.unwrap()) as {
          totalRecords: number;
          data: UserItem[] | null | undefined;
        };

        requestInProgressRef.current = false;

        return response;
      } catch (error) {
        requestInProgressRef.current = false;

        if ((error as { name?: string }).name !== ABORT_ERROR) {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error(ABORT_ERROR);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getUsers,
      searchKey,
      sortInAction,
      activeFilters,
      userIdsString,
      entKeysString,
      riskKeysString,
    ]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (
        !isObserverInView ||
        !isValidApiCall ||
        isGlobalLoading ||
        userIds.length === 0
      ) {
        return;
      }

      handleListApiCall(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setIsListError(false);

          const { totalRecords, data } = response;

          if (!data || data.length === 0) {
            dispatch(
              detailsActions.setEntitlementUsersTotalRecords(totalRecords || 0)
            );

            return;
          }

          let newRows: TrackedUserRowType[];

          try {
            newRows = configureUsersTracker(data) as TrackedUserRowType[];
          } catch (error) {
            Logger.error('Error in data transformation:', error, 'Data:', data);

            return;
          }

          setListRows((prev) => [
            ...prev.map((row) => ({
              ...row,
              tracker: {
                ...row.tracker,
                isFirstAddded: false,
              },
            })),
            ...newRows,
          ]);
          dispatch(
            detailsActions.setEntitlementUsersTotalRecords(totalRecords)
          );
        })
        .catch(createDevRejectionHandler('UsersList: handleListApiCall'));
    }, 100); // Increased debounce time to prevent rapid calls

    return () => clearTimeout(debounceTimer);
  }, [
    dispatch,
    isGlobalLoading,
    isObserverInView,
    isValidApiCall,
    currentOffset,
    handleListApiCall,
    userIds.length,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        batch(() => {
          dispatch(detailsActions.setEntitlementUsersTotalRecords(null));
          dispatch(
            detailsActions.setEntitlementUsersSidePanelRowContext({
              isSidePanelVisible: false,
              rowContext: {} as EntitlementUsersSidePanelGroupRowContextState,
            })
          );
        });
        setListRows([]);
        setIsListError(false);
        setCurrentOffset(0);
        setIsObserverInView(true);
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [
    // Add all the dependencies based on which you want to refresh the page
    dispatch,
    isGlobalLoading,
    activeFilters,
    searchKey,
    sortInAction,
    userIds,
  ]);

  // Removing connectors if any when `list` mode and sidepanel closed
  useEffect(() => {
    if (!isSidePanelVisible) {
      setListRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        }))
      );
    }
  }, [isSidePanelVisible]);

  // Close sidepanel when global loading starts (indicates tab/page change)
  useEffect(() => {
    if (isSidePanelVisible) {
      dispatch(detailsActions.setSidePanelClose());
      dispatch(
        detailsActions.setEntitlementUsersSidePanelRowContext({
          isSidePanelVisible: false,
          rowContext: {} as EntitlementUsersSidePanelGroupRowContextState,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Should not update on these params, one time exectution
  }, [dispatch, isGlobalLoading, activeTab]);

  // Sidepanel handling and row connector lgic on `row` click
  const handleRowClick = useCallback(
    (id: string) => {
      let modifiedIndex: number | undefined;
      const rowContext: EntitlementUsersSidePanelGroupRowContextState = {
        current: null as EntitlementUsersSidePanelRowContextState | null,
      } as EntitlementUsersSidePanelGroupRowContextState;
      const listLength = listRows.length;

      const modifiedRows = listRows.map((row, index) => {
        if (row.tracker.id === id) {
          modifiedIndex = index;

          return {
            ...row,
            tracker: {
              ...row.tracker,
              isRowDetailsOpen: true,
            },
          };
        }

        return {
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        };
      });

      if (modifiedIndex !== undefined) {
        // Will always have the current row
        const { tracker: currentTracker, ...currentRowData } =
          modifiedRows[modifiedIndex];

        rowContext.current = getUserRowContext(currentRowData as UserItem);

        if (modifiedIndex < listLength - 1 && listLength > 1) {
          const { tracker: nextTracker, ...nextRowData } =
            modifiedRows[modifiedIndex + 1];

          rowContext.next = getUserRowContext(nextRowData as UserItem);
        }

        setListRows(() => modifiedRows);

        dispatch(
          detailsActions.setEntitlementUsersSidePanelRowContext({
            isSidePanelVisible: true,
            rowContext,
          })
        );

        if (!isSidePanelVisible) {
          dispatch(detailsActions.setIsSidePanelVisible(!isSidePanelVisible));
        }
      }
    },
    [dispatch, isSidePanelVisible, listRows]
  );

  // Transition logic here
  useEffect(() => {
    if (rowIdToTransition !== null) {
      const nextItem: { tracker?: ListTrackerState } = {};
      const listRowsLength = listRows.length;

      // Start the transition
      setListRows((prevListRows) =>
        prevListRows.map((row, index) => {
          if (row.tracker.id === rowIdToTransition) {
            // Check if next available item is present
            if (index !== listRowsLength - 1 && row.tracker.isRowDetailsOpen) {
              nextItem.tracker = listRows[index + 1].tracker;
            }

            return {
              ...row,
              tracker: { ...row.tracker, isTransition: true },
            };
          }

          return row;
        })
      );

      // End the transition and manage the next item with sidepanel
      setTimeout(() => {
        setListRows((prevListRows) =>
          prevListRows
            .filter((row) => row.tracker.id !== rowIdToTransition)
            .map((row) => {
              if (nextItem.tracker && row.tracker.id === nextItem.tracker.id) {
                return {
                  ...row,
                  tracker: { ...row.tracker, isRowDetailsOpen: true },
                };
              }

              return row;
            })
        );

        dispatch(detailsActions.setEntitlementUsersRowIdToTransition(null));
      }, LEAVE_ANIM_DURATION);
    }
  }, [dispatch, rowIdToTransition, listRows]);

  const getCenterContent = useCallback(
    (item: TrackedUserRowType, listConfigurableColumns: JSX.Element[]) => {
      const { userName, displayName } = item;

      return (
        <Box className='Sod-UsersListGroup-centerContent'>
          <Box className='Sod-UsersListGroup-rowDetails'>
            <Typography kind='h3' color={CF_NEU_BLD}>
              {userName ?? ''}
            </Typography>
            {displayName && (
              <Typography kind='body3' color={CF_SEC_MED}>
                {l(displayName)}
              </Typography>
            )}
          </Box>
          <Box className='Sod-UsersListGroup-rowDetails'>
            {listConfigurableColumns.length > 0 && listConfigurableColumns}
          </Box>
        </Box>
      );
    },
    [l]
  );

  const handleSourceChange = useCallback(
    (sourceType: string, activeTabParam: string, itemParam: UserItem) => {
      dispatch(detailsActions.setSelectedEntitlementUsers(itemParam));

      const { userName } = itemParam;
      const userKey = String(itemParam.userKey);
      const entitlementDisplayName =
        selectedEntitlement?.entitlementDisplayName ??
        selectedEntitlementData?.entitlementDisplayName;

      const isViolationsTab =
        activeTabParam === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS;

      navigateToDetails({
        key: userKey,
        activeTab: activeTabParam,
        isEntitlementRoute: true,
        navigation: {
          source: sourceType,
          ...(userName ? { title: userName } : {}),
          ...(isViolationsTab
            ? {
                ...(entitlementDisplayName
                  ? { entName: entitlementDisplayName }
                  : {}),
                ...(userName ? { userName } : {}),
              }
            : {}),
        },
      });
    },
    [
      dispatch,
      selectedEntitlement?.entitlementDisplayName,
      selectedEntitlementData?.entitlementDisplayName,
      navigateToDetails,
    ]
  );

  const getTrailingContent = useCallback(
    (item: TrackedUserRowType) => (
      <Box className='Sod-UsersListGroup-trailingContent'>
        {/* Placeholder Button */}
        <Box className='Sod-UsersListGroup-rowKabobWrapper'>
          <Box className='Sod-UsersListGroup-rowLinkBlock'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Violations')}
            </Typography>
            <DSLink
              kind='button'
              onClick={() => {
                handleSourceChange(
                  violationsEntitlement,
                  ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
                  item
                );
              }}
              text={String(item.userViolationDetails?.violationCount)}
              size='large'
              color='brand'
            />
          </Box>
        </Box>
      </Box>
    ),
    [l, handleSourceChange]
  );

  // Clear filter on no data
  const handleClearBtn = useCallback(() => {
    dispatch(detailsActions.setEntitlementUsersActiveFilters([]));
  }, [dispatch]);

  // Render body here
  const renderListBody = useCallback(() => {
    // No data Screens
    if ((isZeroCount && !isListLoading) || userIds.length === 0) {
      return (
        <Box className='Sod-UsersList--isEmptyList'>
          <SodMessage
            isSearchActive={isSearchActive}
            isFilterActive={isFilterActive}
            handleFilterClearBtn={handleClearBtn}
            defaultTitle={l('No Users')}
            defaultMessage={l('No users found.')}
          />
        </Box>
      );
    }

    const constantColumnDefs = [
      { label: l('Department'), value: 'departmentName' },
      { label: l('Type'), value: 'identityType' },
    ];

    return (
      <ListRowGroup className='Sod-UsersList--isListRowGroup'>
        {/* List rows */}
        {listRows.map((row) => {
          const {
            tracker: { id, isRowDetailsOpen, isTransition, isFirstAddded },
          } = row;

          const accountCount = row.accountsNames?.length ?? 0;

          const rowClasses = cx(
            'Sod-UsersList-listRow',
            isRowDetailsOpen && 'Sod-UsersList-listRow--isConnected',
            isTransition && 'Sod-UsersList-listRow--isTransition',
            isFirstAddded && 'Sod-UsersList-listRow--isFirstAddded'
          );

          const selectedColumns = activeColumns.filter(
            (column) => column.isChecked
          );

          const constantColumns = constantColumnDefs.map((column, index) => {
            let columnValue = row[column.value as keyof typeof row] ?? null;

            // Convert identityType boolean to External/Internal
            if (column.value === 'identityType' && columnValue !== null) {
              columnValue =
                columnValue === true ? l('External') : l('Internal');
            }

            return (
              <React.Fragment key={column.value}>
                {index !== 0 && <InterPoint color='neutralBold' font='body2' />}
                <SodColumnChip
                  key={column.value}
                  name={column.label}
                  value={columnValue ? String(columnValue) : '-'}
                  isPrefixInterpoint={false}
                />
              </React.Fragment>
            );
          });

          const listConfigurableColumns = selectedColumns
            .filter(
              (column) =>
                column.isChecked &&
                column.value !== 'accountsNames' &&
                column.value !== 'userName' &&
                column.value !== 'departmentName' &&
                column.value !== 'identityType'
            )
            .map((column) => {
              const { label, value } = column;
              let columnValue = row[value as keyof typeof row] ?? null;

              // Handle status field specifically
              if (value === 'userStatus') {
                columnValue = row.status ?? row.userStatus ?? null;
              }

              return (
                <React.Fragment key={value}>
                  <InterPoint color='neutralBold' font='body2' />
                  <SodColumnChip
                    name={label}
                    value={columnValue ? String(columnValue) : '-'}
                    isPrefixInterpoint={false}
                  />
                </React.Fragment>
              );
            });

          const accountChip = accountCount > 0 && (
            <>
              <InterPoint color='neutralBold' font='body2' />
              <SodAccountChip
                key='accountChip'
                visibleAccountName={row.accountsNames?.[0] ?? ''}
                accountCount={accountCount}
                isPrefixInterpoint={false}
              />
            </>
          );

          return (
            <ListRow
              key={id}
              className={rowClasses}
              dataTestId={testIds.SOD_ENTITLEMENT_TAB_USERS_LISTROW}
              isCheckboxDisabled
              leadingComp={
                <SodUserChip
                  key='SodUserChip'
                  firstName={row.firstName ?? ''}
                  lastName={row.lastName ?? ''}
                  userName={row.userName ?? ''}
                  isPrefixInterpoint={false}
                  avatarSize='medium'
                />
              }
              centerContent={
                <Box className='Sod-UsersListGroup-centerContent'>
                  {getCenterContent(row, [
                    ...constantColumns,
                    ...(accountChip ? [accountChip] : []),
                    ...listConfigurableColumns,
                  ])}
                </Box>
              }
              trailingContent={getTrailingContent(row)}
              handleRowClick={() => {
                const { tracker, ...userData } = row;

                dispatch(
                  detailsActions.setEntitlementUsersRowContext(
                    userData as UserItem
                  )
                );
                dispatch(
                  detailsActions.setSelectedEntitlementUsers(
                    userData as UserItem
                  )
                );
                handleRowClick(id);
              }}
              isRowDetailsOpen={isRowDetailsOpen}
              connectorDistance='1.125rem'
              isRowConnectTransitionEnabled
            />
          );
        })}
        {/* Observer Element */}
        {!isListError ? listObserverElement : null}
        {/* Loading Skeleton */}
        {!isListError && isValidApiCall && <SodListLoader count={3} />}
        {/* Retry mechanism */}
        {isListError && (
          <SodReloader triggerRefresh={() => setIsListError(false)} />
        )}
      </ListRowGroup>
    );
  }, [
    isZeroCount,
    isSearchActive,
    isFilterActive,
    handleClearBtn,
    l,
    listRows,
    getCenterContent,
    getTrailingContent,
    handleRowClick,
    listObserverElement,
    isListError,
    isValidApiCall,
    activeColumns,
    dispatch,
    isListLoading,
    userIds.length,
  ]);

  return (
    <Box
      className='Sod-UsersList'
      dataTestId={testIds.SOD_ENTITLEMENT_TAB_USERS}>
      {renderListBody()}
    </Box>
  );
}

export default UsersList;
